import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const faqs = [
  {
    question: "What is SATTVA?",
    answer:
      "SATTVA is an ESG management platform that helps organizations collect, manage, analyze, and report ESG data across their operations and supply chains from a single, centralized system.",
  },
  {
    question: "Who is SATTVA designed for?",
    answer:
      "SATTVA is built for enterprises, financial institutions, manufacturers, and growing organizations—regardless of where they are in their ESG journey.",
  },
  {
    question: "Does SATTVA support Scope 1, 2, and 3 emissions?",
    answer:
      "Yes. SATTVA enables accurate measurement and management of Scope 1, Scope 2, and Scope 3 emissions using structured data collection and reporting workflows.",
  },
  {
    question: "How does SATTVA simplify ESG data collection?",
    answer:
      "SATTVA standardizes ESG data collection across teams, locations, and departments, reducing manual effort and improving data consistency and reliability.",
  },
  {
    question: "Is SATTVA suitable for organizations new to ESG?",
    answer:
      "Yes. SATTVA is designed to support both beginners and mature ESG programs, making ESG adoption structured, manageable, and scalable.",
  },
  {
    question: "Does SATTVA help with ESG compliance and reporting?",
    answer:
      "Yes. SATTVA helps organizations stay audit-ready by maintaining traceable, consistent, and reliable ESG data for reporting and disclosures.",
  },
  {
    question: "Can SATTVA help manage ESG risks?",
    answer:
      "Yes. SATTVA enables organizations to identify, monitor, and manage ESG risks across operations and supply chains in a proactive manner.",
  },
  {
    question: "Is ESG data secure on SATTVA?",
    answer:
      "Yes. SATTVA is built with enterprise-grade security to ensure data confidentiality, integrity, and reliability.",
  },
  {
    question: "Can SATTVA scale as our organization grows?",
    answer:
      "Absolutely. SATTVA is designed to scale with your organization as ESG requirements, operations, and reporting needs evolve.",
  },
  {
    question: "How can we get started with SATTVA?",
    answer:
      "You can get started by talking to our team. We’ll assess your requirements and guide you through onboarding and implementation.",
  },
];

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section className="relative py-8 px-6 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h6 className="text-blue-600 text-base font-medium uppercase mb-2">
            Frequently Asked Questions
          </h6>
          <h3 className="mb-4 md:text-2xl text-xl font-semibold dark:text-white md:mb-0">
            Everything you need to know about SATTVA
          </h3>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-5">
          {faqs.map((faq, index) => {
            const isOpen = activeIndex === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white/70 dark:bg-slate-800/60 backdrop-blur-lg border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex justify-between items-center p-6 text-left"
                >
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                    {faq.question}
                  </h3>

                  {/* Rotating Icon */}
                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-blue-600 text-2xl"
                  >
                    <div><i className={`uil uil-angle-down text-3xl`}></i></div>
                  </motion.div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: "easeInOut" }}
                      className="overflow-hidden px-6"
                    >
                      <div className="text-slate-600 dark:text-slate-300 text-base mb-4">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
